"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import Image, { StaticImageData } from "next/image";

interface AudioPlayerProps {
  audioSrc: string;
  images: {
    play: string | StaticImageData;
    pause: string | StaticImageData;
  };
  initialPlaying?: boolean;
}

export default function AudioPlayer({
  audioSrc,
  images,
  initialPlaying = false,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(initialPlaying);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);

  /* ---------------- RESET WHEN SOURCE CHANGES ---------------- */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.load();
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(initialPlaying);
  }, [audioSrc, initialPlaying]);

  /* ---------------- METADATA + TIME SYNC ---------------- */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => {
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  /* ---------------- PLAY / PAUSE ---------------- */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  const togglePlay = () => {
    setIsPlaying((prev) => !prev);
  };

  /* ---------------- SEEK LOGIC ---------------- */
  const setTimeFromPosition = useCallback(
    (clientX: number) => {
      const trackEl = trackRef.current;
      const audio = audioRef.current;
      if (!trackEl || !audio || !duration) return;

      const rect = trackEl.getBoundingClientRect();
      const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
      const percent = x / rect.width;

      const newTime = percent * duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration],
  );

  useEffect(() => {
    if (!seeking) return;

    const move = (e: MouseEvent | TouchEvent) => {
      if ("touches" in e) {
        const touch = e.touches[0];
        if (touch) setTimeFromPosition(touch.clientX);
      } else {
        setTimeFromPosition(e.clientX);
      }
    };

    const stop = () => setSeeking(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", stop);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", stop);
    };
  }, [seeking, setTimeFromPosition]);

  /* ---------------- DERIVED PROGRESS ---------------- */
  const progress = duration ? (currentTime / duration) * 100 : 0;

  const formatTime = (t: number) => {
    if (!t || !isFinite(t)) return "0:00";
    const mins = Math.floor(t / 60);
    const secs = Math.floor(t % 60)
      .toString()
      .padStart(2, "0");
    return `${mins}:${secs}`;
  };

  return (
    <div className="mt-2 flex items-center gap-4">
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="metadata"
        crossOrigin="anonymous"
      />

      {/* Progress Track */}
      <div
        ref={trackRef}
        onClick={(e) => setTimeFromPosition(e.clientX)}
        className="relative w-full h-[55px] rounded-[55px] border border-[#ECF0F6] bg-white px-6 cursor-pointer"
      >
        <div className="absolute inset-x-6 inset-y-0 flex items-center pr-5">
          <div className="relative w-full h-[6px]">
            <div className="absolute inset-0 bg-[#E6EEF9] rounded-full" />

            <div
              className="absolute left-0 top-0 h-full bg-[#225BD4] rounded-full"
              style={{ width: `${progress}%` }}
            />

            <div
              className="absolute top-1/2 w-[14px] h-[14px] rounded-full bg-[#225BD4]"
              style={{
                left: `${progress}%`,
                transform: "translate(-50%, -50%)",
              }}
              onMouseDown={() => setSeeking(true)}
              onTouchStart={() => setSeeking(true)}
            />
          </div>
        </div>

        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">
          {formatTime(currentTime)}
        </div>
      </div>

      {/* Play Button */}
      <button
        onClick={togglePlay}
        aria-label={isPlaying ? "Pause" : "Play"}
        type="button"
        className="shrink-0"
      >
        <Image
          src={isPlaying ? images.pause : images.play}
          alt={isPlaying ? "Pause" : "Play"}
          width={55}
          height={55}
          priority
          sizes="(max-width: 1024px) 100vw, 50vw"
          fetchPriority="high"
        />
      </button>

      {/* Duration */}
      <div className="text-xs text-slate-500">{formatTime(duration)}</div>
    </div>
  );
}
